import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Cpu, Lightbulb, Play } from "lucide-react";
import { Alert, Button, Card, CardBody, CardHeader, Field, Input, Page, PageHeader, Select, Spinner } from "@/components/ui";
import { useAgentsAndModels, useDatasets, useEvaluationStatus } from "@/components/evaluation/hooks";
import { API_BASE, LIMITS, clamp, modelDisplayName, type Model } from "@/components/evaluation/types";

function LoadingControl({ label }: { label: string }) {
  return (
    <div className="flex h-10 items-center rounded-md border border-input bg-muted px-3">
      <Spinner label={label} />
    </div>
  );
}

export default function EvaluationPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const { datasets } = useDatasets(setError);
  const { agents, models, loadingAgents, loadingModels } = useAgentsAndModels(setError);
  const { activeRunId, setActiveRunId, isEvaluationRunning, setIsEvaluationRunning } = useEvaluationStatus();

  const [selectedAgent, setSelectedAgent] = useState<string>("");
  const [selectedModel, setSelectedModel] = useState<string>("");
  const [selectedDatasetVersion, setSelectedDatasetVersion] = useState<string>("");
  const [evalLimit, setEvalLimit] = useState<number | "">("");
  const [evalRepeat, setEvalRepeat] = useState<number>(1);

  // Auto-select first dataset / first agent once loaded
  useEffect(() => {
    if (datasets.length > 0 && !selectedDatasetVersion) setSelectedDatasetVersion(datasets[0].version);
  }, [datasets]);
  useEffect(() => {
    if (agents.length > 0 && !selectedAgent) setSelectedAgent(agents[0].path);
  }, [agents]);
  // Auto-select model: prefer gemini-3, then flash, then the first model
  useEffect(() => {
    if (models.length > 0 && !selectedModel) {
      const gemini3Model = models.find((m: Model) => m.name.toLowerCase().includes("gemini-3"));
      const flashModel = models.find((m: Model) => m.name.toLowerCase().includes("flash"));
      setSelectedModel(gemini3Model ? gemini3Model.name : flashModel ? flashModel.name : models[0].name);
    }
  }, [models]);

  const getMaxQuestions = () => datasets.find((d) => d.version === selectedDatasetVersion)?.total_questions || 100;
  const currentModel = models.find((m) => m.name === selectedModel);

  const startEvaluation = async () => {
    if (!selectedAgent || !selectedDatasetVersion) {
      setError("Kies een agent en een vragenlijst");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await fetch(`${API_BASE}/api/evaluation/run-evaluation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          agent_path: selectedAgent,
          dataset_version: selectedDatasetVersion,
          model: selectedModel,
          limit: evalLimit || null,
          repeat: evalRepeat,
          parallel_workers: 10, // Managed by Cloud Tasks
        }),
      });

      const result = await response.json();
      if (result.error) {
        setError(result.error);
        return;
      }

      setSuccessMessage(`Evaluatie gestart. Run-ID: ${result.run_id}`);
      setActiveRunId(result.run_id);
      setIsEvaluationRunning(true);

      // Go to the live run view after 1 second (live mode is auto-detected there)
      setTimeout(() => navigate("/runs"), 1000);
    } catch (err: any) {
      console.error("Failed to start evaluation:", err);
      setError(`Evaluatie starten mislukt: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      <PageHeader title="Nieuwe evaluatie" description="Evalueer een agent op een vragenlijst en volg de voortgang live bij Runs." />

      <div className="mb-4 space-y-3">
        {error && (
          <Alert tone="danger" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {successMessage && <Alert tone="success">{successMessage}</Alert>}
        {isEvaluationRunning && activeRunId && (
          <div className="flex flex-col gap-3 rounded-md border border-sl-yellow/60 bg-sl-yellow-soft px-4 py-3 sm:flex-row sm:items-center sm:justify-between" role="status">
            <div className="flex items-center gap-3">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sl-yellow opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-sl-yellow-dark" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold">Er loopt een evaluatie</p>
                <p className="truncate font-mono text-xs text-muted-foreground">Run-ID: {activeRunId}</p>
              </div>
            </div>
            <Button size="sm" onClick={() => navigate("/runs")}>
              Bekijk bij Runs
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader title="Instellingen" description="Kies wat je wilt evalueren." />
          <CardBody className="space-y-5">
            <Field label="Agent" htmlFor="eval-agent">
              {loadingAgents ? (
                <LoadingControl label="Agents laden…" />
              ) : (
                <Select id="eval-agent" value={selectedAgent} onChange={(e) => setSelectedAgent(e.target.value)}>
                  {agents.map((agent) => (
                    <option key={agent.path} value={agent.path}>
                      {agent.display_name} ({agent.path})
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field label="Gemini-model" htmlFor="eval-model" hint={!loadingModels ? currentModel?.description : undefined}>
              {loadingModels ? (
                <LoadingControl label="Modellen laden…" />
              ) : (
                <Select id="eval-model" value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)}>
                  {models.map((model) => (
                    <option key={model.name} value={model.name}>
                      {modelDisplayName(model)}
                      {model.input_token_limit && ` (${(model.input_token_limit / 1000000).toFixed(1)}M tokens)`}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field label="Vragenlijst" htmlFor="eval-dataset">
              <Select id="eval-dataset" value={selectedDatasetVersion} onChange={(e) => setSelectedDatasetVersion(e.target.value)}>
                {datasets.map((ds) => (
                  <option key={ds.version} value={ds.version}>
                    {ds.version} – {ds.name} ({ds.total_questions} vragen)
                  </option>
                ))}
              </Select>
            </Field>

            <div className="grid gap-5 sm:grid-cols-2">
              <Field
                label="Aantal vragen (optioneel)"
                htmlFor="eval-limit"
                hint={`Beperk het aantal vragen (1–${getMaxQuestions()}), of laat leeg voor alle vragen.`}
              >
                <Input
                  id="eval-limit"
                  type="number"
                  value={evalLimit}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "") {
                      setEvalLimit("");
                    } else {
                      const num = parseInt(val);
                      if (!isNaN(num)) setEvalLimit(clamp(num, LIMITS.LIMIT_MIN, getMaxQuestions()));
                    }
                  }}
                  min={LIMITS.LIMIT_MIN}
                  max={getMaxQuestions()}
                  placeholder="Alle vragen"
                />
              </Field>

              <Field label="Herhalingen" htmlFor="eval-repeat" hint={`Hoe vaak elke vraag wordt gesteld (${LIMITS.REPEAT_MIN}–${LIMITS.REPEAT_MAX}).`}>
                <Input
                  id="eval-repeat"
                  type="number"
                  value={evalRepeat}
                  onChange={(e) => {
                    const num = parseInt(e.target.value);
                    if (!isNaN(num)) setEvalRepeat(clamp(num, LIMITS.REPEAT_MIN, LIMITS.REPEAT_MAX));
                  }}
                  min={LIMITS.REPEAT_MIN}
                  max={LIMITS.REPEAT_MAX}
                />
              </Field>
            </div>

            <div className="border-t border-border pt-5">
              <Button
                variant="brand"
                size="lg"
                className="w-full"
                onClick={startEvaluation}
                loading={loading}
                disabled={!selectedAgent || !selectedDatasetVersion || isEvaluationRunning}
              >
                {!loading && !isEvaluationRunning && <Play className="h-4 w-4" />}
                {loading ? "Evaluatie starten…" : isEvaluationRunning ? "Evaluatie bezig…" : "Evaluatie starten"}
              </Button>
              {isEvaluationRunning && (
                <p className="mt-2 text-center text-sm text-muted-foreground">Wacht tot de huidige evaluatie klaar is voordat je een nieuwe start.</p>
              )}
            </div>
          </CardBody>
        </Card>

        <div className="space-y-6">
          {selectedModel && currentModel && (
            <Card>
              <CardBody className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-sl-charcoal text-sl-yellow">
                  <Cpu className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Gekozen model</p>
                  <h4 className="mt-0.5 truncate font-semibold">{modelDisplayName(currentModel, selectedModel)}</h4>
                  <p className="mt-1 text-sm text-muted-foreground">{currentModel.description || "Generatief model van Google Gemini"}</p>
                  {currentModel.input_token_limit && (
                    <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
                      <div className="rounded-md bg-muted px-2.5 py-1.5">
                        <dt className="text-muted-foreground">Invoer</dt>
                        <dd className="font-semibold tabular-nums">{(currentModel.input_token_limit / 1000000).toFixed(1)}M tokens</dd>
                      </div>
                      {currentModel.output_token_limit && (
                        <div className="rounded-md bg-muted px-2.5 py-1.5">
                          <dt className="text-muted-foreground">Uitvoer</dt>
                          <dd className="font-semibold tabular-nums">{(currentModel.output_token_limit / 1000).toFixed(0)}K tokens</dd>
                        </div>
                      )}
                    </dl>
                  )}
                </div>
              </CardBody>
            </Card>
          )}

          <Card>
            <CardHeader icon={<Lightbulb className="h-5 w-5 text-sl-yellow-dark" />} title="Tips" />
            <CardBody>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex gap-2">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-sl-yellow" />
                  Gebruik 3 vragen voor een snelle test tijdens de ontwikkeling.
                </li>
                <li className="flex gap-2">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-sl-yellow" />
                  Verhoog het aantal herhalingen (3–5) om de betrouwbaarheid te testen (max. {LIMITS.REPEAT_MAX}).
                </li>
                <li className="flex gap-2">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-sl-yellow" />
                  Vragen worden parallel verwerkt, maximaal {LIMITS.WORKERS_MAX} tegelijk.
                </li>
                <li className="flex gap-2">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-sl-yellow" />
                  Volg de resultaten live bij Runs.
                </li>
              </ul>
            </CardBody>
          </Card>
        </div>
      </div>
    </Page>
  );
}
